import React from "react";
import { Compass, Heart, Users, Sparkles } from "lucide-react";
import { PageHeader, Section, Card, PlaceholderImage, Prose, ButtonLink } from "../components/ui-kit";
import { IMAGES, LOGO } from "../data/site";
import { TeamSection } from "../components/TeamSection";

const VALUES = [
  { icon: Heart, title: "Respect", text: "We see the whole person and treat everyone with dignity and warmth." },
  { icon: Compass, title: "Independence", text: "We help people we support do more for themselves, in their own way." },
  { icon: Users, title: "Belonging", text: "We build friendships and connection so no one feels on the outside." },
  { icon: Sparkles, title: "Aspiration", text: "We believe in possibility and support people to reach for their goals." },
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About us"
        title="About Spire Inclusion"
        intro="We are a person-centred day service supporting young adults with disabilities to live active, meaningful weekdays built around independence, wellbeing and belonging."
        testid="about-header"
      />
      <Section tone="default">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Prose>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">Who we are</h2>
            <p>
              Spire Inclusion provides weekday support, Monday to Friday, for young adults with
              disabilities. We focus on active, meaningful days that support life skills, confidence,
              social connection, wellbeing and involvement in the local community.
            </p>
            <p>
              We use the words "people we support" because that is what we do. Support is shaped
              around each person, their interests and their goals, never a one-size-fits-all
              programme.
            </p>
          </Prose>
          <figure className="relative overflow-hidden rounded-3xl bg-orange-50 border border-stone-200 aspect-[4/3] flex items-center justify-center p-10">
            <img
              src={LOGO}
              alt="Spire Inclusion logo: a colourful circular emblem of stylised people reaching upwards together."
              className="max-h-full max-w-full object-contain"
            />
          </figure>
        </div>
      </Section>

      <Section tone="white">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">What guides us</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            A simple set of values shapes our days and our decisions.
          </p>
        </div>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <Card key={v.title} data-testid={`value-card-${v.title.toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
                <v.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-stone-900">{v.title}</h3>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">{v.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <TeamSection />

      <Section tone="tinted">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Our approach</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            We take time to get to know each person, listen to families and carers, and work in
            partnership with social workers, commissioners and referral partners. Together we build a
            week that feels purposeful and enjoyable.
          </p>
          <ButtonLink to="/contact" className="mt-8" data-testid="about-contact-button">
            Make an enquiry
          </ButtonLink>
        </div>
      </Section>
    </>
  );
}
